import React from "react";

import type { SvgIconProps } from "@mui/material";
import Box from "@mui/material/Box";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { ICON_BY_CATEGORY } from "@app/analytics/components/Icons";
import { pluralize, pluralizePhrase } from "@app/common/utils";
import { formatNumber } from "@app/utils/utils";

import { Row, StackProps } from "@rui/Stack";
import { Img } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

type StyledChipProps = StackProps & { active: boolean };

const StyledChip = styled<React.FC<StyledChipProps>>(
  ({ active: _a, ...props }) => <Row variant="left" {...props} />
)(({ active, theme }) => ({
  alignItems: "center",
  backgroundColor: active
    ? theme.palette.shades.meadow[50]
    : theme.palette.transparent.black[4],
  color: active
    ? theme.palette.shades.meadow[600]
    : theme.palette.shades.neutral[400],
  borderRadius: theme.corners.small,
  padding: theme.spacing("2px", 0.5),
  "&:hover": {
    cursor: "default",
  },
  "& .chip__icon": {
    fontSize: "12px",
    color: "inherit",
  },
}));

export const CountChip = ({
  Icon,
  count,
  label,
  tooltip,
}: {
  Icon: React.FunctionComponent<SvgIconProps>;
  count: number | null;
  label?: string;
  tooltip?: string;
}) => {
  const isActive = count != null;
  const formattedCount = count == null ? "-" : formatNumber(count);
  return (
    <TooltipWrapper show={isActive} placement="top" title={tooltip}>
      <StyledChip active={isActive} spacing={0.5}>
        <Icon className="chip__icon" />
        <Typography variant="labelReg" noWrap>
          {formattedCount} {label ? pluralize(label, count ?? 0) : ""}
        </Typography>
      </StyledChip>
    </TooltipWrapper>
  );
};

export const ActionItemCountChip = ({ count }: { count: number | null }) => (
  <CountChip
    Icon={ICON_BY_CATEGORY.action_items.Icon}
    count={count}
    tooltip={`Identified ${pluralizePhrase(count ?? 0, "action item")}`}
  />
);

export const KeyQuestionCountChip = ({ count }: { count: number | null }) => (
  <CountChip
    Icon={ICON_BY_CATEGORY.key_questions.Icon}
    count={count}
    tooltip={`Identified ${pluralizePhrase(count ?? 0, "key question")}`}
  />
);

type ProgressIconProps = StackProps & {
  iconSrc: string;
  step: number;
  error?: boolean;
};

export const ProgressIcon = styled<React.FC<ProgressIconProps>>(
  ({ iconSrc, step: _s, error: _e, ...props }) => (
    <Row {...props} variant="center">
      <Row className="progress__icon">
        <Img src={iconSrc} />
      </Row>
      <Box className="progress__step progress__step-1" />
      <Box className="progress__step progress__step-2" />
      <Box className="progress__step progress__step-3" />
    </Row>
  )
)(({ error, step, theme }) => {
  const backgroundColor = error
    ? theme.palette.shades.negative[50]
    : step > 3
      ? theme.palette.shades.meadow[50]
      : "#FFF";

  return {
    height: "24px",
    width: "24px",
    position: "relative",
    overflow: "hidden",
    borderRadius: theme.corners.round,
    backgroundColor,
    "& .progress__icon": {
      backgroundColor,
      borderRadius: theme.corners.round,
      zIndex: 1,
      padding: "4px",
      "& img": {
        height: "12px",
        width: "12px",
      },
    },
    "& .progress__step": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: theme.corners.round,
      clipPath:
        "polygon(calc(50% + 2px/2) 50%, calc(50% + 2px/2) 0%, 100% 0%, 100% calc(78.665% - 2px/2), 50% calc(50% - 2px/2))",
    },
    "& .progress__step-1": {
      transform: "rotate(-20deg)",
      backgroundColor: error
        ? theme.palette.error.main
        : step > 1
          ? theme.palette.shades.meadow[500]
          : theme.palette.shades.primary[500],
    },
    "& .progress__step-2": {
      transform: "rotate(100deg)",
      backgroundColor: error
        ? theme.palette.error.main
        : step === 2
          ? theme.palette.shades.primary[500]
          : step < 2
            ? theme.palette.shades.neutral[100]
            : theme.palette.shades.meadow[500],
    },
    "& .progress__step-3": {
      transform: "rotate(220deg)",
      backgroundColor: error
        ? theme.palette.error.main
        : step === 3
          ? theme.palette.shades.primary[500]
          : step < 3
            ? theme.palette.shades.neutral[100]
            : theme.palette.shades.meadow[500],
    },
  };
});
